"use client";

import { SkeletonLoader } from "@/components/ui/skeleton";
import useContainerWidth from "@/hooks/use-container-width";
import { useNearbySUPs } from "@/hooks/use-nearby-sups";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { type NearbyProperty } from "@/types/sup";
import { useState } from "react";
import { SUPCard } from "../sup/card";
import { NextButton, PreviousButton } from "./pagination-buttons";
import { ProjectCard } from "./sp-card";

interface NearbySUPsClientProps {
	coordinates: {
		lat: number;
		lng: number;
	};
	initialData?: NearbyProperty[];
	loading?: boolean;
	distanceText?: string;
	type: "unit" | "sp";
	excludeId?: string;
	shouldPrioritizeCoverImage?: boolean; // Add prop to control image prioritization
	isNotAvailableForSale?: boolean;
	shouldRedirect?: boolean;
	unitId?: string; // Optional: enables ranked/filtered results
}

export function NearbySUPsClient({
	coordinates,
	initialData = [],
	loading: parentLoading = false,
	distanceText,
	type,
	excludeId,
	shouldPrioritizeCoverImage = false, // Default to false for below-the-fold content
	isNotAvailableForSale = false,
	shouldRedirect,
	unitId,
}: NearbySUPsClientProps) {
	const {
		data: sups,
		error,
		hasMore,
		hasPrevious,
		loadingMore,
		loadingPrevious,
		loadMore,
		loadPrevious,
		retry,
	} = useNearbySUPs({
		coordinates,
		type,
		unitId, // Optional - enables ranking when provided
		initialData,
		initialError: null,
		excludeId,
	});

	const [currentPage, setCurrentPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const { containerRef, containerWidth } = useContainerWidth();

	// Calculate columns based on container width instead of device breakpoints
	// This matches our @container CSS breakpoints: @lg (512px) and @4xl (896px)
	const columns = containerWidth < 512 ? 1 : containerWidth < 896 ? 2 : 3;
	const rowsPerPage = isNotAvailableForSale ? 2 : 1;
	const itemsPerPage = columns * rowsPerPage;
	const openPropertyOverlay = usePropertyInteractionStore(state => state.openPropertyOverlay);

	// Calculate pagination
	const totalPages = Math.ceil((sups?.length || 0) / itemsPerPage);
	const startIndex = currentPage * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentItems = sups?.slice(startIndex, endIndex) || [];

	const handleNextPage = async () => {
		if (currentPage < totalPages - 1) {
			setCurrentPage(currentPage + 1);
		} else if (hasMore && !loadingMore) {
			setIsLoading(true);
			await loadMore();
			setIsLoading(false);
		}
	};

	const handlePrevPage = async () => {
		if (currentPage > 0) {
			setCurrentPage(currentPage - 1);
		} else if (hasPrevious && !loadingPrevious) {
			setIsLoading(true);
			await loadPrevious();
			setIsLoading(false);
		}
	};

	// Show skeleton while parent is loading
	if (parentLoading) {
		return (
			<div>
				<div className="py-2.5 border-b border-primary/20">
					<SkeletonLoader height="20px" width="120px" />
				</div>
				<div className="mt-3">
					<div className="grid grid-cols-1 @lg:hidden gap-4">
						{Array.from({ length: 1 }).map((_, index) => (
							<div key={index}>
								<SkeletonLoader height="200px" className="rounded-lg mb-4" />
								<SkeletonLoader height="16px" width="80%" className="mb-2" />
								<SkeletonLoader height="14px" width="60%" />
							</div>
						))}
					</div>
					<div className="hidden @lg:grid-cols-2 @4xl:hidden gap-4">
						{Array.from({ length: 2 }).map((_, index) => (
							<div key={index}>
								<SkeletonLoader height="200px" className="rounded-lg mb-4" />
								<SkeletonLoader height="16px" width="80%" className="mb-2" />
								<SkeletonLoader height="14px" width="60%" />
							</div>
						))}
					</div>
					<div className="hidden @lg:grid-cols-2 @4xl:grid-cols-3 gap-4">
						{Array.from({ length: 3 }).map((_, index) => (
							<div key={index}>
								<SkeletonLoader height="200px" className="rounded-lg mb-4" />
								<SkeletonLoader height="16px" width="80%" className="mb-2" />
								<SkeletonLoader height="14px" width="60%" />
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div ref={containerRef}>
			{error && (
				<div className="text-red-500 text-sm mt-1">
					{error} -{" "}
					<button type="button" onClick={retry} className="underline hover:text-red-600">
						Try again
					</button>
				</div>
			)}

			{sups && sups.length > 0 ? (
				<div className="mt-3 relative">
					{isLoading && (
						<div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-20 bg-white/80 rounded-full p-2 shadow-md">
							<div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
						</div>
					)}

					{/* Pagination Controls */}
					<div className="flex justify-between items-center mb-4">
						<div className="text-sm text-gray-600">
							{/* Mobile: Show single item format when only 1 item displayed */}
							<span className="sm:hidden">
								Showing{" "}
								{startIndex + 1 === Math.min(endIndex, sups.length)
									? `${startIndex + 1} of ${sups.length} `
									: `${startIndex + 1}-${Math.min(endIndex, sups.length)} of ${sups.length} `}
								{type === "sp" ? "projects" : "units"}
							</span>
							{/* Tablet/Desktop: Always show range format */}
							<span className="hidden sm:inline">
								Showing {startIndex + 1}-{Math.min(endIndex, sups.length)} of {sups.length}{" "}
								{type === "sp" ? "projects" : "units"}
							</span>
						</div>
						<div className="flex gap-2">
							<PreviousButton
								hasPrevious={currentPage > 0 || hasPrevious}
								isLoading={isLoading || loadingPrevious}
								loadPrevious={handlePrevPage}
							/>
							<NextButton
								hasMore={currentPage < totalPages - 1 || hasMore}
								isLoading={isLoading || loadingMore}
								loadMore={handleNextPage}
							/>
						</div>
					</div>

					{/* Grid Layout */}
					<div className="grid grid-cols-1 @lg:grid-cols-2 @4xl:grid-cols-3 gap-4 mb-6">
						{currentItems.map((sup, index) => {
							return (
								<div
									key={`${sup._id}-${index}`}
									className="cursor-pointer transition-all duration-300 hover:shadow-xl rounded-2xl overflow-hidden"
									onClick={() => {
										if (shouldRedirect) return;

										openPropertyOverlay({
											sups: [sup],
											initialSupId: sup._id,
											projectName: sup.projectName,
										});
									}}
								>
									<div className="overflow-hidden rounded-2xl">
										{type === "sp" ? (
											<ProjectCard
												data={sup}
												distanceFromText={distanceText}
												shouldPrioritizeCoverImage={shouldPrioritizeCoverImage}
												shouldRedirect={shouldRedirect}
											/>
										) : (
											<SUPCard
												data={sup}
												distanceFromText={distanceText}
												shouldPrioritizeCoverImage={shouldPrioritizeCoverImage}
												shouldRedirect={shouldRedirect}
											/>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			) : !loadingMore ? (
				<div className="mt-3 text-center py-8 text-gray-500">
					<p>No nearby {type === "sp" ? "projects" : "units"} found</p>
				</div>
			) : null}
		</div>
	);
}
